// src/features/media/components/ClipRangeSelector.tsx

import { Stack, Group, Text, RangeSlider, Button } from '@mantine/core';
import { MediaUtils } from '../MediaUtils';

interface ClipRangeSelectorProps {
  duration: number; // Total video duration in seconds
  startSecond: number; // Current in point
  endSecond: number; // Current out point
  onChange: (start: number, end: number) => void;
  onSetIn?: () => void; // Set in point at current player time
  onSetOut?: () => void; // Set out point at current player time
}

/**
 * Timeline slider with read-only time displays for clip selection.
 * Primary interaction is dragging the range handles.
 */
export function ClipRangeSelector({
  duration,
  startSecond,
  endSecond,
  onChange,
  onSetIn,
  onSetOut,
}: ClipRangeSelectorProps) {
  const clipDuration = endSecond - startSecond;

  return (
    <Stack gap="xs">
      {/* Time displays with Set buttons */}
      <Group justify="space-between">
        <Group gap="xs">
          <Text size="sm" fw={500}>
            In:{' '}
            <Text span ff="monospace">
              {MediaUtils.formatTimecode(startSecond)}
            </Text>
          </Text>
          {onSetIn && (
            <Button variant="light" size="compact-xs" onClick={onSetIn}>
              Set
            </Button>
          )}
        </Group>
        <Group gap="xs">
          {onSetOut && (
            <Button variant="light" size="compact-xs" onClick={onSetOut}>
              Set
            </Button>
          )}
          <Text size="sm" fw={500}>
            Out:{' '}
            <Text span ff="monospace">
              {MediaUtils.formatTimecode(endSecond)}
            </Text>
          </Text>
        </Group>
      </Group>

      {/* Range slider */}
      <RangeSlider
        min={0}
        max={duration}
        step={1}
        minRange={1}
        value={[startSecond, endSecond]}
        onChange={([start, end]) => onChange(start, end)}
        label={(value) => MediaUtils.formatTimecode(value)}
        labelAlwaysOn={false}
        marks={[
          { value: 0, label: MediaUtils.formatTimecode(0) },
          { value: duration, label: MediaUtils.formatTimecode(duration) },
        ]}
        styles={{
          markLabel: { fontSize: 'var(--mantine-font-size-xs)' },
        }}
        mb="lg"
      />

      {/* Duration display */}
      <Text size="sm" fw={500}>
        Clip Duration: {MediaUtils.formatTimecode(clipDuration)}
      </Text>
    </Stack>
  );
}
